import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import {
    getUserQuestions,
    isUserInputToolName,
} from '@apps/web/components/phoebe_chat/user_question_model';
import { getUserQuestionPresentation } from '@apps/web/features/phoebe_chat_v3/model/question_presentation';
import { type V3MessageEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';

/**
 * Rebuild the clicked CTA label from the saved raw answer so it can render as
 * a durable user message without changing the value submitted to the agent.
 */
export function getResolvedQuestionActionEntry(
    entry: ToolExecutionEntry,
): V3MessageEntry | null {
    if (
        !isUserInputToolName(entry.name) ||
        entry.status !== 'completed' ||
        !entry.isComplete ||
        entry.error ||
        !isRecord(entry.output) ||
        entry.output['status'] !== 'answered'
    ) {
        return null;
    }
    const sourceQuestions = getUserQuestions(entry);
    const presentation = getUserQuestionPresentation(entry, sourceQuestions);
    const answer = entry.output['answer'];
    if (presentation === null) {
        return null;
    }
    const selectedLabels =
        presentation.type === 'list'
            ? (Array.isArray(answer) ? answer : [answer])
                  .filter((value): value is string => typeof value === 'string')
                  .map(
                      (value) =>
                          presentation.items.find((item) => item.id === value)
                              ?.title,
                  )
                  .filter((value): value is string => value !== undefined)
            : typeof answer === 'string'
              ? presentation.actions
                    .filter((action) => action.value === answer)
                    .map((action) => action.label)
              : [];
    if (selectedLabels.length === 0) {
        return null;
    }
    return {
        kind: 'message',
        id: `question-answer:${entry.callId ?? entry.id}`,
        createdAt: entry.createdAt,
        role: 'user',
        content: selectedLabels.join(', '),
        references: entry.references,
        author: null,
        isError: false,
        pendingDeliveryMode: null,
        sourceItemId: null,
        revision: JSON.stringify([answer, selectedLabels, entry.references]),
    };
}
